/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Modal, Button } from 'react-bootstrap';
import Spinner from 'react-bootstrap/Spinner';
import { Form } from 'react-bootstrap-v5';
import Select from 'react-select';
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper';
import validationErrorMsg from '../helpers/ValidationErrorMsg';

interface CostCenterModalProps {
  show: boolean;
  handleClose: (isSuccess: boolean) => void;
  initialData?: {
    idCostCenters?: number;
    name?: string;
    details?: string | null;
    idProjects?: string | number | null;
    projectName?: string | null;
  };
}

interface ProjectOption {
  label: string;
  value: string;
}

interface FormState {
  name: string;
  details: string;
  idProjects: string;
}

const CostCenterCreate = ({ show, handleClose, initialData }: CostCenterModalProps) => {
  const mode = initialData?.idCostCenters ? 'edit' : 'create';

  const initialState: FormState = {
    name: initialData?.name ?? '',
    details: initialData?.details ?? '',
    idProjects: initialData?.idProjects ? String(initialData.idProjects) : '',
  };

  const [formData, setFormData] = useState<FormState>(initialState);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(
    initialData?.idProjects
      ? { label: initialData.projectName ?? 'Selected Project', value: String(initialData.idProjects) }
      : null
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      fetchProjects();
      setFormData({
        name: initialData?.name ?? '',
        details: initialData?.details ?? '',
        idProjects: initialData?.idProjects ? String(initialData.idProjects) : '',
      });
      setSelectedProject(
        initialData?.idProjects
          ? { label: initialData.projectName ?? 'Selected Project', value: String(initialData.idProjects) }
          : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialData]);

  const fetchProjects = () => {
    fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
      .then((resp) => resp.json())
      .then((resp) => {
        const items = (resp?.data || []).map((item: any) => ({
          label: item.name,
          value: String(item.id),
        }));
        setProjects(items);
      })
      .catch((error) => {
        console.log(error, 'catch the hoop');
      });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProjectChange = (project: ProjectOption | null) => {
    setSelectedProject(project);
    setFormData((prev) => ({
      ...prev,
      idProjects: project?.value ?? '',
    }));
  };

  useEffect(() => {
    if (projects.length > 0 && formData.idProjects) {
      const option = projects.find((p) => p.value === formData.idProjects);
      if (option) {
        setSelectedProject(option);
      }
    }
  }, [projects, formData.idProjects]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    const payload = {
      name: formData.name,
      details: formData.details,
      idProjects: formData.idProjects || null,
    };

    const requestOptions = postRequestOptions(payload);
    if (mode === 'edit' && initialData?.idCostCenters) {
      requestOptions.method = 'PUT';
    }

    const endpoint =
      mode === 'edit' && initialData?.idCostCenters
        ? `${process.env.REACT_APP_API_URL}cost-centers/${initialData.idCostCenters}`
        : `${process.env.REACT_APP_API_URL}cost-centers`;

    try {
      const response = await fetch(endpoint, requestOptions);
      const data = await response.json();

      if (response.ok && data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          html: data?.successMessage || `Cost center ${mode === 'edit' ? 'updated' : 'created'} successfully.`,
        });
        handleClose(true);
      } else {
        const errorsMessage =
          validationErrorMsg(data.errors ?? []).join('<br>') ||
          data?.errorMessage ||
          `Unable to ${mode === 'edit' ? 'update' : 'create'} cost center.`;
        Swal.fire({
          icon: 'error',
          title: 'Error',
          html: errorsMessage,
        });
      }
    } catch (error) {
      console.log(error, 'catch the hoop');
      Swal.fire({
        icon: 'error',
        title: 'Error',
        html: 'Something went wrong. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={() => handleClose(false)} size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{mode === 'edit' ? 'Edit Cost Center' : 'Create Cost Center'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="costCenterProject">
            <Form.Label>Select Project</Form.Label>
            <Select
              value={selectedProject}
              onChange={handleProjectChange}
              options={projects}
              menuPosition="fixed"
              isClearable={true}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="costCenterName">
            <Form.Label>Name<span className="required text-danger"></span></Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="costCenterDetails">
            <Form.Label>Details</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="details"
              value={formData.details}
              onChange={handleChange}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <button type="submit" className="btn btn-sm btn-primary me-2" disabled={loading}>
            {loading && (
              <Spinner
                as="span"
                animation="grow"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            )}
            <span> {mode === 'edit' ? 'Update' : 'Submit'}</span>
          </button>
          <Button variant="secondary" size="sm" onClick={() => handleClose(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CostCenterCreate;
